import React, {useState} from 'react';
import classNames from 'classnames';
import {
    useClpPurchaseContext,
    useClpSelectedOption,
    CLPPurchaseOption,
} from '../../context/clp-purchase-context.react-component';
import {useI18n} from '@udemy-v2/i18n';
import {TextSkeleton} from '@udemy-v2/react-reveal-components';
import {useCommonData} from '@udemy-v2/common-data';
import styles from './course-sidebar-container.module.scss';
import {CollapsibleBuyBox} from '../buy-box/collapsible-buy-box.react-component';
import {CollapsibleSubscriptionPurchaseContent} from '@/features/course-landing-page/components/subscription-purchase-content/collapsible-subscription-purchase-content.react-component';
import {CollapsibleNonCatalogPPContent} from '@/features/course-landing-page/components/non-catalog-pp-offer';
import {ClpIconSecondaryActions} from '@/features/course-landing-page/components/clp-secondary-actions/clp-icon-secondary-actions.react-component';
import {GrowWithGoogleEnrollmentBox} from '@/features/course-landing-page/components/grow-with-google-enrollment-box';
import {CourseContentPlacement} from '@/features/course-landing-page/course-landing-page.react-component';
import {useFeatures} from '@udemy-v2/react-experimentation';

export const CourseSidebarCollapsibleActionsContainerSkeleton = () => {
    return (
        <div className={styles.container}>
            <TextSkeleton
                className={styles.skeleton}
                withTitle={true}
                lineCountPerParagraph={3}
                paragraphCount={4}
            />
        </div>
    );
};

/**
 * CourseSidebarCollapsibleActionsContainer component - this renders for enrolled, free, and non-Personal Plan paid courses
 * @constructor
 */
export const CourseSidebarCollapsibleActionsContainer = ({}) => {
    const {gettext} = useI18n();
    const commonData = useCommonData();
    const {
        isLoading,
        isCourseInAvailablePlans,
        isEligibleForNonCatalogPPOffer,
        isCourseIncludedInGrowWithGoogleSubscription,
    } = useClpPurchaseContext();
    const {selectedPurchaseOption} = useClpSelectedOption();

    const isSubscriptionPrimary = selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION;

    const {featureVariantAssignments: features} = useFeatures(['keep_price_box_position']);

    const keepPriceBoxPosition = features.get('keep_price_box_position');
    const keepPriceBoxPositionEnabled = keepPriceBoxPosition?.configuration?.keepPosition ?? false;

    // TODO: Remove this state and logic once the flag is removed.
    // Captures the initial layout once so `keep_price_box_position` can pin the primary slot and
    // stop the box from jumping when the user toggles. We skip the LOADING value because
    // `selectedPurchaseOption` lags one render behind the resolved default option, which would
    // otherwise mis-pin subs-first MX courses to the buy box.
    const [initialPrimaryWasSubscription, setInitialPrimaryWasSubscription] = useState<
        boolean | null
    >(null);
    if (
        !isLoading &&
        selectedPurchaseOption !== CLPPurchaseOption.LOADING &&
        initialPrimaryWasSubscription === null
    ) {
        setInitialPrimaryWasSubscription(isSubscriptionPrimary);
    }

    const primarySlotIsSubscription =
        keepPriceBoxPositionEnabled && initialPrimaryWasSubscription !== null
            ? initialPrimaryWasSubscription
            : isSubscriptionPrimary;

    const showSubCopy =
        commonData.user?.country === 'IN' || commonData.visitor.countryCode === 'IN';

    const subscriptionContent = isCourseInAvailablePlans && (
        <CollapsibleSubscriptionPurchaseContent isSubCopyEnabled={showSubCopy} />
    );

    const hasMultiplePurchaseOptions =
        isCourseInAvailablePlans || isEligibleForNonCatalogPPOffer;
    const transactionalContent = <CollapsibleBuyBox showRadio={hasMultiplePurchaseOptions} />;

    let primaryContent: React.ReactNode;
    let secondaryContent: React.ReactNode;

    if (isEligibleForNonCatalogPPOffer) {
        const nonCatalogSubsContent = (
            <CollapsibleNonCatalogPPContent isSubCopyEnabled={showSubCopy} />
        );
        primaryContent = primarySlotIsSubscription ? nonCatalogSubsContent : transactionalContent;
        secondaryContent = primarySlotIsSubscription ? transactionalContent : nonCatalogSubsContent;
    } else if (isCourseInAvailablePlans) {
        primaryContent = primarySlotIsSubscription ? subscriptionContent : transactionalContent;
        secondaryContent = primarySlotIsSubscription ? transactionalContent : subscriptionContent;
    } else {
        primaryContent = transactionalContent;
        secondaryContent = null;
    }

    if (isLoading) {
        return <CourseSidebarCollapsibleActionsContainerSkeleton />;
    }
    return (
        <>
            {isCourseIncludedInGrowWithGoogleSubscription ? (
                <div className={classNames(styles.container)}>
                    <GrowWithGoogleEnrollmentBox placement={CourseContentPlacement.SIDEBAR} />
                </div>
            ) : (
                <>
                    <span className={classNames(styles['collapsible-title'], 'ud-heading-xl')}>
                        {gettext('Purchase options')}
                    </span>
                    <div className={styles['collapsible-container']}>
                        <div>
                            {primaryContent}
                            {secondaryContent}
                        </div>
                        <div>
                            <ClpIconSecondaryActions
                                className={styles['collapsible-secondary-actions']}
                            />
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
